<template>
    <div class="content mk-ref-report">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t("marketing.referral.report.title") }}</h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-breadcrumb></route-breadcrumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <!-- filter bar -->
            <card class="mb-3">
                <div class="row">
                    <div class="col-xl-4 col-md-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.referral.report.range") }}</label>
                        <el-date-picker v-model="f.range" type="daterange" value-format="yyyy-MM-dd" format="yyyy-MM-dd" class="w-100" :clearable="false" unlink-panels data-testid="rr-range"></el-date-picker>
                    </div>
                    <div class="col-xl-4 col-md-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.referral.report.cohort") }}</label>
                        <el-select v-model="f.cohort" class="w-100" data-testid="rr-cohort">
                            <el-option value="all" :label="$t('marketing.referral.report.cohort_all')"></el-option>
                            <el-option value="signed_up_in_range" :label="$t('marketing.referral.report.cohort_range')"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-4 col-md-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.referral.report.group_by") }}</label>
                        <el-select v-model="f.group_by" class="w-100" data-testid="rr-group">
                            <el-option v-for="g in groups" :key="g" :value="g" :label="$t('marketing.referral.report.group.' + g)"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-3 col-md-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.referral.report.owner") }}</label>
                        <el-select
                            v-model="f.owner_bo_user_id"
                            class="w-100"
                            filterable
                            remote
                            clearable
                            :remote-method="searchOwners"
                            :placeholder="$t('marketing.referral.report.all')"
                            @visible-change="(o) => o && !owners.length && searchOwners('')"
                        >
                            <el-option value="(none)" :label="$t('marketing.referral.none_label')"></el-option>
                            <el-option v-for="o in owners" :key="o.id" :value="o.id" :label="o.name"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-3 col-md-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.referral.report.team") }}</label>
                        <el-select v-model="f.team" class="w-100" filterable clearable :placeholder="$t('marketing.referral.report.all')">
                            <el-option value="(none)" :label="$t('marketing.referral.none_label')"></el-option>
                            <el-option v-for="t in teams" :key="t" :value="t" :label="t"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-3 col-md-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.referral.report.campaign") }}</label>
                        <el-select v-model="f.campaign" class="w-100" filterable clearable :placeholder="$t('marketing.referral.report.all')">
                            <el-option value="(none)" :label="$t('marketing.referral.none_label')"></el-option>
                            <el-option v-for="c in campaigns" :key="c" :value="c" :label="c"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-3 col-md-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.referral.report.code") }}</label>
                        <el-select
                            v-model="f.code_id"
                            class="w-100"
                            filterable
                            remote
                            clearable
                            :remote-method="searchCodes"
                            :placeholder="$t('marketing.referral.report.all')"
                            data-testid="rr-code"
                            @visible-change="(o) => o && !codes.length && searchCodes('')"
                        >
                            <el-option v-for="c in codes" :key="c.id" :value="c.id" :label="c.code + (c.name ? ' — ' + c.name : '')"></el-option>
                        </el-select>
                    </div>
                    <div class="col-12 d-flex align-items-end" style="gap: 8px;">
                        <base-button type="primary" size="sm" :loading="loading" data-testid="rr-search" @click="run"><i class="fa fa-search"></i> {{ $t("marketing.referral.report.search") }}</base-button>
                        <base-button type="secondary" size="sm" :loading="exporting" :disabled="!summary" data-testid="rr-export" @click="exportCsv"><i class="fa fa-file-csv"></i> {{ $t("marketing.referral.report.export") }}</base-button>
                    </div>
                </div>
                <small v-if="rangeError" class="text-danger">{{ $t("marketing.referral.report.invalid_range") }}</small>
            </card>

            <el-alert v-if="schemaMissing" type="error" :closable="false" show-icon class="mb-3" :title="$t('marketing.schema_missing')" data-testid="schema-missing"></el-alert>

            <template v-if="summary">
                <div class="row">
                    <div v-for="c in cardDefs" :key="c.key" class="col-xl col-md-3 col-6">
                        <card class="mb-3 rr-stat" body-classes="py-3">
                            <div class="text-muted small">{{ $t("marketing.referral.report.card." + c.key) }}</div>
                            <div class="h3 mb-0" :data-card="c.key">{{ c.value }}<small v-if="c.sub" class="text-muted ml-1">{{ c.sub }}</small></div>
                        </card>
                    </div>
                </div>

                <card class="mb-3" body-classes="px-0 pb-0">
                    <el-table
                        :data="summary.rows"
                        class="bon-table bon-tight rr-table"
                        header-row-class-name="thead-light"
                        show-summary
                        :summary-method="totalsRow"
                        :empty-text="$t('marketing.referral.report.empty')"
                        row-class-name="rr-click-row"
                        data-testid="rr-table"
                        @row-click="openMembers"
                    >
                        <el-table-column v-for="col in keyCols" :key="col" :label="$t('marketing.referral.report.col.' + col)" :min-width="col === 'name' ? 200 : col === 'code' ? 120 : 140">
                            <template v-slot="{ row }">
                                <span :class="{ 'rr-mono': col === 'code' }">{{ keyText(row.key, col) }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column v-for="m in metricCols" :key="m.prop" :label="$t('marketing.referral.report.col.' + m.label)" :min-width="m.money ? 128 : 112" align="right">
                            <template v-slot="{ row }">{{ cell(row, m) }}</template>
                        </el-table-column>
                    </el-table>
                    <div v-if="summary.truncated" class="px-4 pt-2 small text-warning">{{ $t("marketing.referral.report.truncated") }}</div>
                    <p class="px-4 py-2 mb-0 small text-muted" data-testid="rr-footnote">{{ $t("marketing.referral.report.footnote") }}</p>
                </card>
            </template>
        </div>

        <!-- members of one row. §5.5 says el-drawer; Element here is 2.4.11
             (Drawer arrived in 2.8), so a wide el-dialog plays the part (phase 1 did the same). -->
        <el-dialog :title="drawerTitle" :visible.sync="drawer" width="1100px" top="4vh" custom-class="rr-members-dialog" append-to-body>
            <div data-testid="rr-members">
                <div class="d-flex justify-content-between align-items-center mb-2" style="gap: 8px;">
                    <el-select v-model="memberSort" size="small" style="width: 200px;" @change="loadMembers(1)">
                        <el-option value="signed_up_at" :label="$t('marketing.referral.report.m.signed_up_at')"></el-option>
                        <el-option value="orders" :label="$t('marketing.referral.report.m.orders')"></el-option>
                        <el-option value="revenue_completed" :label="$t('marketing.referral.report.m.revenue_completed')"></el-option>
                    </el-select>
                    <base-button type="secondary" size="sm" :loading="exportingMembers" data-testid="rr-members-export" @click="exportMembers"><i class="fa fa-file-csv"></i> {{ $t("marketing.referral.report.export_members") }}</base-button>
                </div>
                <el-table v-loading="membersLoading" :data="members" size="small" class="bon-table bon-tight rr-table" :empty-text="$t('marketing.referral.report.empty')" data-testid="rr-members-table">
                    <el-table-column :label="$t('marketing.referral.report.m.name')" min-width="130">
                        <template v-slot="{ row }">
                            <span>{{ row.name || "-" }}</span>
                            <div v-if="row.deleted" class="small text-muted">{{ $t("marketing.referral.report.deleted") }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.report.m.phone')" width="136">
                        <template v-slot="{ row }"><span class="rr-mono text-nowrap" data-testid="rr-phone">{{ phone(row.phone_masked) }}</span></template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.report.m.signed_up_at')" width="132">
                        <template v-slot="{ row }"><span class="small">{{ fmt(row.signed_up_at) }}</span></template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.report.m.channel')" width="88">
                        <template v-slot="{ row }">{{ tOr("marketing.referral.report.channel." + row.channel, row.channel) }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.report.m.source')" width="84">
                        <template v-slot="{ row }">{{ tOr("marketing.referral.report.source." + row.source, row.source) }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.report.m.code')" width="104">
                        <template v-slot="{ row }"><span class="rr-mono">{{ row.code }}</span></template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.report.m.coupon')" min-width="160">
                        <template v-slot="{ row }">
                            <el-tag size="mini" :type="rewardTag(row.reward_status)">{{ tOr("marketing.referral.reward_status." + row.reward_status, row.reward_status) }}</el-tag>
                            <span v-if="row.reward_status === 'granted' && row.reward_coupon_used" class="small text-muted ml-1">{{ $t("marketing.referral.reward_status.coupon_used") }}</span>
                            <template v-if="row.reward_status === 'failed'">
                                <div class="small text-danger">{{ tOr("marketing.referral.reward_status.error." + row.reward_error, row.reward_error) }}</div>
                                <base-button v-if="canWrite && canRetry(row)" size="sm" type="warning" class="mt-1" :loading="retrying === row.redemption_id" data-testid="rr-retry" @click="retry(row)">{{ $t("marketing.referral.reward_status.retry") }}</base-button>
                            </template>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.report.m.orders')" width="84" align="right" prop="orders"></el-table-column>
                    <el-table-column :label="$t('marketing.referral.report.m.completed')" width="76" align="right" prop="orders_completed"></el-table-column>
                    <el-table-column :label="$t('marketing.referral.report.m.cancelled_returned')" width="112" align="right">
                        <template v-slot="{ row }">{{ (Number(row.orders_cancelled) || 0) + (Number(row.orders_returned) || 0) }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.report.m.revenue_placed')" width="100" align="right">
                        <template v-slot="{ row }">{{ money(row.revenue_placed) }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.report.m.revenue_completed')" width="100" align="right">
                        <template v-slot="{ row }">{{ money(row.revenue_completed) }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.report.m.last_order')" width="132">
                        <template v-slot="{ row }"><span class="small">{{ fmt(row.last_order_at) }}</span></template>
                    </el-table-column>
                </el-table>
                <el-pagination class="mt-2 text-right" layout="total, prev, pager, next" :total="membersTotal" :page-size="20" :current-page="membersPage" @current-change="loadMembers"></el-pagination>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { DatePicker, Select, Option, Table, TableColumn, Alert, Dialog, Pagination, Tag, Loading } from "element-ui";
import { mapGetters } from "vuex";
import { ReferralCodeService, ReferralReportService } from "@/resource";
import { apiErrorCode, defaultReportRange, fmtBkk, isSchemaMissing } from "@/utils/marketing";
import {
    REFERRAL_GROUPS,
    buildReferralReportQuery,
    canRetryGrant,
    drillFilters,
    maskedPhone,
    referralCards,
    referralKeyColumns,
    referralKeyValue,
    rewardStatusTag
} from "@/utils/referral";

const METRICS = [
    { prop: "signups", label: "signups" },
    { prop: "signups_rewarded", label: "signups_rewarded" },
    { prop: "members_ordered", label: "members_ordered" },
    { prop: "conversion_pct", label: "conversion", pct: true },
    { prop: "orders", label: "orders" },
    { prop: "orders_open", label: "open" },
    { prop: "orders_completed", label: "completed" },
    { prop: "orders_cancelled", label: "cancelled" },
    { prop: "orders_returned", label: "returned" },
    { prop: "revenue_placed", label: "revenue_placed", money: true },
    { prop: "revenue_completed", label: "revenue_completed", money: true },
    { prop: "revenue_cancelled_returned", label: "revenue_cancelled_returned", money: true },
    { prop: "aov_placed", label: "aov", money: true }
];

const fmtMoney = (n) => (Number(n) || 0).toLocaleString("th-TH", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const fmtInt = (n) => (Number(n) || 0).toLocaleString("th-TH");

// การตลาด → รายงานรหัสแนะนำ (Phase 2 spec 2026-10-13 §5.5 / §3.6).
export default {
    name: "MarketingReferralReport",
    directives: { loading: Loading.directive },
    components: {
        [DatePicker.name]: DatePicker,
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Alert.name]: Alert,
        [Dialog.name]: Dialog,
        [Pagination.name]: Pagination,
        [Tag.name]: Tag
    },
    data() {
        return {
            f: { range: defaultReportRange(), cohort: "all", group_by: "code", owner_bo_user_id: null, team: "", campaign: "", code_id: null },
            groups: REFERRAL_GROUPS,
            metricCols: METRICS,
            loading: false,
            exporting: false,
            rangeError: false,
            schemaMissing: false,
            summary: null,
            lastParams: null,
            owners: [],
            teams: [],
            campaigns: [],
            codes: [],
            drawer: false,
            drawerRow: null,
            members: [],
            membersTotal: 0,
            membersPage: 1,
            membersLoading: false,
            memberSort: "signed_up_at",
            exportingMembers: false,
            retrying: null
        };
    },
    computed: {
        ...mapGetters({ canWrite: "Auth/canWriteMarketing" }),
        keyCols() {
            return referralKeyColumns((this.lastParams && this.lastParams.group_by) || this.f.group_by);
        },
        cardDefs() {
            const c = referralCards(this.summary && this.summary.totals);
            return [
                { key: "signups", value: fmtInt(c.signups) },
                { key: "signups_rewarded", value: fmtInt(c.signups_rewarded) },
                { key: "members_ordered", value: fmtInt(c.members_ordered), sub: `(${fmtMoney(c.conversion_pct)}%)` },
                { key: "orders", value: fmtInt(c.orders) },
                { key: "revenue_placed", value: fmtMoney(c.revenue_placed) },
                { key: "revenue_completed", value: fmtMoney(c.revenue_completed) },
                { key: "revenue_cancelled_returned", value: fmtMoney(c.revenue_cancelled_returned) }
            ];
        },
        drawerTitle() {
            const base = this.$t("marketing.referral.report.drawer_title");
            if (!this.drawerRow) return base;
            return base + " — " + this.keyCols.slice(0, 2).map((c) => this.keyText(this.drawerRow.key, c)).join(" / ");
        }
    },
    created() {
        const q = this.$route.query;
        const codeId = Number(q.code_id);
        if (Number.isInteger(codeId) && codeId > 0) {
            this.f.code_id = codeId;
            this.f.group_by = "code";
            this.preloadCode(codeId);
        }
        this.loadMeta();
        this.run();
    },
    methods: {
        fmt: fmtBkk,
        money: fmtMoney,
        phone: maskedPhone,
        rewardTag: rewardStatusTag,
        canRetry: canRetryGrant,
        tOr(key, fallback) {
            return this.$te(key) ? this.$t(key) : fallback || "-";
        },
        keyText(key, col) {
            const v = referralKeyValue(key, col);
            if (v) return v;
            return col === "code" || col === "name" ? "-" : this.$t("marketing.referral.none_label");
        },
        cell(row, m) {
            const v = row[m.prop];
            if (v === null || v === undefined) return "—";
            if (m.pct) return fmtMoney(v) + "%";
            return m.money ? fmtMoney(v) : fmtInt(v);
        },
        totalsRow({ columns }) {
            const t = (this.summary && this.summary.totals) || {};
            const nKey = this.keyCols.length;
            return columns.map((col, i) => {
                if (i === 0) return this.$t("marketing.referral.report.total");
                if (i < nKey) return "";
                const m = METRICS[i - nKey];
                return m ? this.cell(t, m) : "";
            });
        },
        async loadMeta() {
            try {
                const res = await ReferralCodeService.meta();
                const d = (res.data && res.data.data) || {};
                this.teams = d.teams || [];
                this.campaigns = d.campaigns || [];
            } catch (e) {
                /* filters stay empty */
            }
        },
        async searchOwners(q) {
            try {
                const res = await ReferralCodeService.owners(q);
                this.owners = (res.data && res.data.data) || [];
            } catch (e) {
                /* keep */
            }
        },
        async searchCodes(q) {
            try {
                const res = await ReferralCodeService.list({ q: q || undefined, page: 1, limit: 50 });
                const found = (res.data && res.data.data) || [];
                const keep = this.codes.filter((c) => c.id === this.f.code_id && !found.some((x) => x.id === c.id));
                this.codes = keep.concat(found);
            } catch (e) {
                /* keep */
            }
        },
        async preloadCode(id) {
            try {
                const res = await ReferralCodeService.get(id);
                const c = res.data && res.data.data;
                if (c && !this.codes.some((x) => x.id === c.id)) this.codes = [c].concat(this.codes);
            } catch (e) {
                /* the filter still works by id */
            }
        },
        async run() {
            const q = buildReferralReportQuery(this.f);
            this.rangeError = !q.ok;
            if (!q.ok) return;
            this.loading = true;
            this.lastParams = q.params;
            try {
                const res = await ReferralReportService.summary(q.params);
                this.summary = (res.data && res.data.data) || { rows: [], totals: {} };
                this.schemaMissing = false;
            } catch (err) {
                this.summary = null;
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else if (apiErrorCode(err) === "invalid_range" || (err && err.response && err.response.status === 400)) this.rangeError = true;
                else this.$notify({ type: "danger", message: this.$t("marketing.referral.report.load_error") });
            } finally {
                this.loading = false;
            }
        },
        memberQuery(extra) {
            const p = this.lastParams;
            return buildReferralReportQuery({
                ...p,
                range: [p.from, p.to],
                ...drillFilters(this.drawerRow && this.drawerRow.key, p.group_by),
                sort: this.memberSort,
                ...extra
            });
        },
        openMembers(row) {
            if (!this.lastParams) return;
            this.drawerRow = row;
            this.memberSort = "signed_up_at";
            this.drawer = true;
            this.loadMembers(1);
        },
        async loadMembers(page) {
            this.membersPage = page;
            this.membersLoading = true;
            const q = this.memberQuery({ page, limit: 20 });
            try {
                const res = await ReferralReportService.members(q.params);
                this.members = (res.data && res.data.data) || [];
                this.membersTotal = (res.data && res.data.total) || 0;
            } catch (e) {
                this.members = [];
                this.membersTotal = 0;
                this.$notify({ type: "danger", message: isSchemaMissing(e) ? this.$t("marketing.schema_missing") : this.$t("marketing.referral.report.load_error") });
            } finally {
                this.membersLoading = false;
            }
        },
        async retry(row) {
            this.retrying = row.redemption_id;
            try {
                const res = await ReferralCodeService.retryGrant(row.redemption_id);
                const upd = res.data && res.data.data;
                if (upd) this.members = this.members.map((m) => (m.redemption_id === row.redemption_id ? { ...m, ...upd } : m));
                const ok = upd && upd.reward_status === "granted";
                this.$notify({ type: ok ? "success" : "warning", message: ok ? this.$t("marketing.referral.reward_status.retried") : this.tOr("marketing.referral.reward_status.error." + (upd && upd.reward_error), this.$t("marketing.referral.err.generic")) });
            } catch (err) {
                const code = apiErrorCode(err);
                this.$notify({ type: "danger", message: code && this.$te("marketing.referral.err." + code) ? this.$t("marketing.referral.err." + code) : this.$t("marketing.referral.err.generic") });
            } finally {
                this.retrying = null;
            }
        },
        download(res, name) {
            const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        async exportCsv() {
            if (!this.lastParams) return;
            this.exporting = true;
            try {
                this.download(await ReferralReportService.exportCsv(this.lastParams), `referral-report-${this.lastParams.from}_${this.lastParams.to}.csv`);
            } catch (err) {
                this.$notify({ type: "danger", message: isSchemaMissing(err) ? this.$t("marketing.schema_missing") : this.$t("marketing.referral.report.load_error") });
            } finally {
                this.exporting = false;
            }
        },
        async exportMembers() {
            const q = this.memberQuery({});
            this.exportingMembers = true;
            try {
                this.download(await ReferralReportService.exportMembersCsv(q.params), `referral-members-${q.params.from}_${q.params.to}.csv`);
            } catch (err) {
                this.$notify({ type: "danger", message: isSchemaMissing(err) ? this.$t("marketing.schema_missing") : this.$t("marketing.referral.report.load_error") });
            } finally {
                this.exportingMembers = false;
            }
        }
    }
};
</script>

<style scoped>
.rr-stat .h3 {
    white-space: nowrap;
}
.rr-mono {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    letter-spacing: 0.04em;
}
/* Thai has no spaces between words: let a narrow cell scroll the table instead of breaking "สมัครใหม่" mid-word. */
.rr-table >>> th .cell,
.rr-table >>> .el-table__footer .cell {
    white-space: nowrap;
}
.mk-ref-report >>> .rr-click-row {
    cursor: pointer;
}
</style>
